// Expensesummery.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

const Expensesummery = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const printRef = useRef();

  const formatCurrency = useCallback((amount) => {
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [company]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setCompany(res.data);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.startDate = fromDate;
      if (toDate) params.endDate = toDate;
      params.limit = 1000;

      const res = await axios.get(`${url}/api/expense`, {
        headers: getAuthHeaders(),
        params,
      });
      const data = res.data.data || [];
      const formatted = data.map(exp => ({
        _id: exp._id,
        expense_date: exp.expense_date,
        description: exp.description || '—',
        account_name: exp.account?.account_name || '—',
        expense_type: exp.expense_name,
        amount: exp.amount || 0,
      }));
      setExpenses(formatted);
      setFilteredData(formatted);
      if (formatted.length === 0) setError('No expenses found for the selected date range.');
      else setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load expenses');
      setExpenses([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleGenerate = () => fetchExpenses();

  const handlePrint = () => window.print();

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const primaryColor = '#f1592a';
  const secondaryColor = '#2e3192';

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  const totalAmount = filteredData.reduce((sum, exp) => sum + (exp.amount || 0), 0);

  if (loadingCompany) {
    return <div style={{ color: secondaryColor, textAlign: 'center', marginTop: '2rem' }}>Loading company data...</div>;
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ==================================================
          SCREEN PREVIEW (with filters, sidebar-friendly)
      ================================================== */}
      <div className="screen-only" ref={printRef}>
        {/* HEADER */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {getLogoUrl() && (
                <img
                  src={getLogoUrl()}
                  alt="Logo"
                  style={{ height: '64px', maxWidth: '80px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: secondaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>{company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{company?.address || 'No address'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {company?.contact || 'No phone'}
                {company?.contact_2 ? ` · ${company.contact_2}` : ''}
                {company?.contact_3 ? ` · ${company.contact_3}` : ''}
                <br />
                ✉️ {company?.admin_email || 'No email'}
              </p>
              <p>Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}</p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e9ecef', paddingTop: '0.5rem' }}>
            <h2 style={{ textAlign: 'center', color: primaryColor }}>Expense Summary</h2>
          </div>
        </div>

        {/* FILTERS */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ color: secondaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <label style={{ color: secondaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleGenerate} disabled={loading} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', padding: '0.5rem', borderRadius: '0.375rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>{loading ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={filteredData.length === 0} style={{ backgroundColor: secondaryColor, color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredData.length === 0 ? 0.6 : 1 }}>🖨️ Print</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>
            * Date range filters expense transaction dates.
          </div>
        </div>

        {/* TABLE (screen) */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
          {error && <div style={{ padding: '1rem', color: 'red', textAlign: 'center' }}>{error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: secondaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Account</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Expense Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && !loading && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>No expenses found. Adjust date filters and click Generate Report.</td>
                    </tr>
                  )}
                  {filteredData.map((exp, idx) => (
                    <tr key={exp._id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ padding: '0.75rem' }}>{formatDate(exp.expense_date)}</td>
                      <td style={{ padding: '0.75rem' }}>{exp.description}</td>
                      <td style={{ padding: '0.75rem' }}>{exp.account_name}</td>
                      <td style={{ padding: '0.75rem' }}>{exp.expense_type}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(exp.amount)}</td>
                    </tr>
                  ))}
                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="5" style={{ padding: '0.75rem', textAlign: 'right' }}>Total:</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalAmount)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {getLogoUrl() && (
              <img
                src={getLogoUrl()}
                alt="Logo"
                className="print-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || 'Company Name'}
              </h1>
              <p className="print-company-line">{company?.address || 'No address'}</p>
              <p className="print-company-line">
                {company?.contact ? `Tel: ${company.contact}` : ''}
                {company?.contact_2 ? ` · ${company.contact_2}` : ''}
                {company?.contact_3 ? ` · ${company.contact_3}` : ''}
              </p>
              <p className="print-company-line">
                {company?.admin_email ? `Email: ${company.admin_email}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">
                {company?.fiscal_year_start
                  ? `${new Date(company.fiscal_year_start).getFullYear()}-${new Date(company.fiscal_year_start).getFullYear() + 1}`
                  : `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`}
              </span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{getCompanyCurrency(company)}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">EXPENSE SUMMARY</h2>
          <p className="print-report-subtitle">
            {fromDate && toDate
              ? `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`
              : fromDate
              ? `From ${new Date(fromDate).toLocaleDateString()}`
              : toDate
              ? `Until ${new Date(toDate).toLocaleDateString()}`
              : 'All Time'}
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th className="print-col-date">Date</th>
              <th className="print-col-desc">Description</th>
              <th className="print-col-account">Account</th>
              <th className="print-col-type">Expense Type</th>
              <th className="print-col-money">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="6" className="print-empty">
                  No expenses found for the selected date range.
                </td>
              </tr>
            ) : (
              filteredData.map((exp, idx) => (
                <tr key={exp._id}>
                  <td className="print-col-no">{idx + 1}</td>
                  <td className="print-col-date">{formatDate(exp.expense_date)}</td>
                  <td className="print-col-desc">{exp.description}</td>
                  <td className="print-col-account">{exp.account_name}</td>
                  <td className="print-col-type">{exp.expense_type}</td>
                  <td className="print-col-money">{formatCurrency(exp.amount)}</td>
                </tr>
              ))
            )}
            {filteredData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="5" className="print-total-label">TOTAL</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalAmount)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ==================================================
          INLINE PRINT STYLES
      ================================================== */}
      <style>{`
        /* Hide print block on screen */
        .print-only { display: none; }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, search, filters, etc.) */
          body * {
            visibility: hidden !important;
          }

          /* Show only the print block */
          .print-only,
          .print-only * {
            visibility: visible !important;
          }

          /* Position the print block at top-left of the page */
          .print-only {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
            background: #ffffff !important;
          }

          /* Explicitly hide anything marked screen-only */
          .screen-only,
          .no-print,
          .hide-on-print {
            display: none !important;
          }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111111;
            padding: 0;
            max-width: 100%;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222222;
            margin-bottom: 5mm;
          }
          .print-header-left {
            display: flex;
            align-items: center;
            gap: 6mm;
            flex: 1;
          }
          .print-logo {
            max-height: 18mm;
            max-width: 26mm;
            object-fit: contain;
          }
          .print-company-name {
            font-size: 14pt;
            font-weight: 700;
            color: #170f48;
            margin: 0 0 1mm 0;
            letter-spacing: 0.3pt;
          }
          .print-company-line {
            font-size: 8.5pt;
            color: #444444;
            margin: 0.4mm 0;
          }
          .print-header-right {
            text-align: right;
            font-size: 8.5pt;
          }
          .print-meta-item {
            display: flex;
            justify-content: flex-end;
            gap: 4mm;
            margin-bottom: 1mm;
          }
          .print-meta-label { color: #666666; font-weight: 500; }
          .print-meta-value {
            color: #111111;
            font-weight: 700;
            min-width: 26mm;
            text-align: right;
          }

          .print-title-block {
            text-align: center;
            margin-bottom: 5mm;
          }
          .print-report-title {
            font-size: 14pt;
            font-weight: 700;
            letter-spacing: 2pt;
            color: #170f48;
            margin: 0 0 1mm 0;
          }
          .print-report-subtitle {
            font-size: 9.5pt;
            color: #555555;
            margin: 0;
          }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999999;
            padding: 3.5pt 5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 8.5pt;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }
          .print-col-no      { width: 10mm; text-align: center; }
          .print-col-date    { width: 24mm; }
          .print-col-account { width: 30mm; }
          .print-col-type    { width: 30mm; }
          .print-col-money   {
            width: 28mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }
          .print-total-row {
            background: #f4f4f4 !important;
            font-weight: 700;
          }
          .print-total-label {
            text-align: right;
            letter-spacing: 0.5pt;
          }
          .print-total-value { font-weight: 700; }
          .print-empty {
            text-align: center;
            padding: 12pt;
            font-style: italic;
          }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 10mm; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 5mm;
          }
          .print-signature {
            flex: 1;
            text-align: center;
            font-size: 8.5pt;
            color: #333333;
          }
          .print-signature-line {
            border-top: 0.5pt solid #333333;
            margin-bottom: 1.5mm;
            height: 10mm;
          }
          .print-footer-note {
            text-align: center;
            font-size: 7.5pt;
            color: #888888;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Expensesummery;
